import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { deleteRequest, downloadBlobGet, fetchTextGet } from '../../../api/ApiController';
import { DatabaseContext } from '../../../redux/DatabaseStore';
import { ensureTrainingInvitationTemplatesLoaded, refreshTrainingInvitationTemplates } from '../../../utils/trainingInvitationTemplatesLookup';
import { ReactTabulator, reactFormatter } from 'react-tabulator';
import '../../../assets/css/style.css';
import '../../../assets/css/iconsmenu.css';
import 'primereact/resources/themes/lara-light-indigo/theme.css';
import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';
import { confirmDialog } from 'primereact/confirmdialog';
import { Dialog } from 'primereact/dialog';
import { MdSearch, MdSearchOff } from 'react-icons/md';
import { DateTime } from 'luxon';
import { AddIcon, EditIcon, CopyIcon, DeleteIcon, PrintIcon, SearchIcon, ExportIcon, VerticalLineIcon, BackToFirstIcon, BackIcon, ForwardIcon, ForwardToLastIcon, FavoriteIcon, PriceIcon, EditRowIcon, RefreshIcon } from '../../svg/svgIcons';
import { Avatar } from 'primereact/avatar';
import { PanelMenu } from 'primereact/panelmenu';
import UtilContext from '../../../components/UtilContext';
import AuthContext from '../../../components/AuthContext';
import { LocaleContext } from '../../../components/LocaleContext';
import IntersectionObserverWrapper from '../../../components/toolbar/IntersectionObserverWrap';
import { scrollToFirst as scrollToFirstUtil, scrollUp as scrollUpUtil, scrollDown as scrollDownUtil, scrollToLast as scrollToLastUtil } from '../../../utils/tabulatorScroll';
import { OverflowMenuProvider } from '../../../components/toolbar/OverflowMenu';
import { getEffectiveMask } from '../../../utils/effectiveMask';
import { applyUserMaskRowToTabulator, cleanSorters, safeParse } from '../../../utils/tabulatorProgressiveLoading';
import { getTabulatorPersistLayoutEvents } from '../../../utils/tabulatorPersistLayout';
import { getTabulatorLayoutForStandardMask } from '../../../utils/tabulatorLayoutUtils';
import { dateUserHeaderFilter } from '../../../utils/dateUserHeaderFilter';
import { inputOnEnterHeaderFilter } from '../../../utils/inputOnEnterHeaderFilter';
import { tabulatorColumnMinWidthFromTitle, TABULATOR_LIST_ID_MIN_WIDTH, tabulatorDateUserColumnMinWidth } from '../../../utils/tabulatorColumnMinWidth';
import PrintOptionsDialog from '../../../components/PrintOptionsDialog';
import { buildExportQueryStringFromParams, openPrintHtmlWindow, triggerBlobDownload } from '../../../utils/serviceTaskExport';
import { EXPORT_DOCUMENT_TITLE_KEYS, xlsxDownloadFilename } from '../../../utils/exportExcelNaming';
import TabulatorMaskDialog from '../../../utils/tabulatorMaskDialog';
import { resetTabulatorMask } from '../../../utils/tabulatorResetMask';
import { formatDate as formatDateUtil } from '../../../utils/dateUtils';
import { applyTabulatorRuntimeI18n } from '../../../utils/tabulatorRuntimeI18n';


const PAGE_LIST_KEY = 'TrainingInvitationTemplateList';
const ADD_MASK = 'trng030';
const TABLE_HEIGHT = 'calc(100vh - 193px)';

const DEFAULT_COLUMNS_VISIBLE_INVITATION_TEMPLATE = Object.freeze({
  id: true,
  name: true,
  description: true,
  content: true,
  created_at: true,
  updated_at: true,
});

function mergeVisibility(saved) {
  const merged = { ...DEFAULT_COLUMNS_VISIBLE_INVITATION_TEMPLATE };
  if (saved && typeof saved === 'object') {
    Object.keys(DEFAULT_COLUMNS_VISIBLE_INVITATION_TEMPLATE).forEach((k) => {
      if (Object.prototype.hasOwnProperty.call(saved, k)) merged[k] = saved[k] !== false;
    });
  }
  if (!Object.values(merged).some(Boolean)) return { ...DEFAULT_COLUMNS_VISIBLE_INVITATION_TEMPLATE };
  return merged;
}

function normalizeInvitationTemplateColumnVisibilityForSave(stateObj) {
  const out = {};
  for (const field of Object.keys(DEFAULT_COLUMNS_VISIBLE_INVITATION_TEMPLATE)) out[field] = stateObj?.[field] !== false;
  return out;
}

function columnsFromTabulatorForStandardMaskSave(raw) {
  const out = {};
  for (const field of Object.keys(DEFAULT_COLUMNS_VISIBLE_INVITATION_TEMPLATE)) out[field] = raw?.[field] !== false;
  return out;
}

function clearHeaderFilterForField(table, field) {
  if (!table || field == null) return;
  try { table.setHeaderFilterValue?.(field, '', true); } catch (_) {}
}

function applyDataColumnVisibilityToTable(table, visibilityMap) {
  if (!table) return;
  for (const f of Object.keys(DEFAULT_COLUMNS_VISIBLE_INVITATION_TEMPLATE)) {
    if (visibilityMap[f] === false) {
      clearHeaderFilterForField(table, f);
      table.hideColumn?.(f);
    } else {
      table.showColumn?.(f);
    }
  }
  setTimeout(() => table.redraw?.(true), 0);
}

function countVisibleDataColumns(vis) {
  if (!vis || typeof vis !== 'object') return 0;
  return Object.keys(DEFAULT_COLUMNS_VISIBLE_INVITATION_TEMPLATE).filter((k) => vis[k] !== false).length;
}

const normalizeText = (value) => String(value ?? '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

const INVITATION_TEMPLATE_MASK_FIELD_I18N = Object.freeze({
  id: 'TrainingInvitationTemplateList.Column.Id',
  name: 'TrainingInvitationTemplateList.Column.Name',
  description: 'TrainingInvitationTemplateList.Column.Description',
  content: 'TrainingInvitationTemplateList.Column.Content',
  created_at: 'TrainingInvitationTemplateList.Column.CreatedAt',
  updated_at: 'TrainingInvitationTemplateList.Column.UpdatedAt',
});

const INVITATION_TEMPLATE_COLUMN_I18N_KEYS = INVITATION_TEMPLATE_MASK_FIELD_I18N;

export default function TrainingInvitationTemplateList() {
  const [state, dispatch] = useContext(DatabaseContext);
  const { user } = useContext(AuthContext);
  const { t, locale } = useContext(LocaleContext);
  const { setDialogText, setVisible, openFavoriteDialog, toast, setFastView, setUserMaskFilter, setStandardMaskFilter, openChosenNotificationPreferences, getDateFormat, formatDate, getUserColor, getUserInitials } = useContext(UtilContext);

  const ref = useRef(null);
  const overlayRef = useRef(null);
  const tableContainerRef = useRef(null);
  const isRestoringRef = useRef(false);
  const submittedSearchValueRef = useRef('');
  const lastContainerHeightRef = useRef(0);

  const [editIcon, setEditIcon] = useState(true);
  const [deleteIcon, setDeleteIcon] = useState(true);
  const [copyIcon, setCopyIcon] = useState(true);
  const [searchIconColor, setSearchIconColor] = useState('');
  const [visibleSearch, setVisibleSearch] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [maskVisible, setMaskVisible] = useState(false);
  const [columnsVisible, setColumnsVisible] = useState({ ...DEFAULT_COLUMNS_VISIBLE_INVITATION_TEMPLATE });
  const [expandedKeys, setExpandedKeys] = useState({});
  const [printChoiceVisible, setPrintChoiceVisible] = useState(false);
  const [printOrientationChoice, setPrintOrientationChoice] = useState('landscape');
  const [printLayoutChoice, setPrintLayoutChoice] = useState('branded');

  const getTable = useCallback(() => ref.current?.table ?? ref.current, []);
  const dateFmt = getDateFormat?.(state?.module_settings) ?? 'dd.MM.yyyy';
  const dateTimeFmt = `${dateFmt} HH:mm`;
  const canSetStandardMask = user?.permissions?.includes('set standard mask');
  const hasApplicableStandardMask = useMemo(() => {
    const row = (state.standard_mask_filters || []).find((m) => m.page === PAGE_LIST_KEY);
    if (!row) return false;
    const attributes = safeParse(row.attributes, []);
    if (!Array.isArray(attributes) || attributes.length <= 1) return false;
    const cols = safeParse(row.columns, {});
    const merged = mergeVisibility(cols && typeof cols === 'object' ? cols : {});
    return Object.values(merged).some(Boolean);
  }, [state.standard_mask_filters]);

  const textHeaderFilter = useCallback((headerValue, rowValue) => {
    if (!headerValue) return true;
    return normalizeText(rowValue).includes(normalizeText(headerValue));
  }, []);

  const idHeaderFilter = useCallback((headerValue, rowValue, rowData) => {
    if (!headerValue) return true;
    return String(rowData?.id ?? '').includes(String(headerValue).trim());
  }, []);

  const dateUserColumnFilter = useCallback((headerValue, rowValue, rowData, filterParams) => {
    if (!headerValue) return true;
    const raw = String(headerValue || '').trim();
    if (raw.startsWith('range:')) {
      const p = raw.slice('range:'.length).split(':');
      if (p.length >= 2 && p[0] && p[1]) {
        const from = DateTime.fromFormat(p[0], 'yyyy-MM-dd').startOf('day');
        const to = DateTime.fromFormat(p[1], 'yyyy-MM-dd').endOf('day');
        const dt = DateTime.fromISO(String(rowData?.[filterParams?.name] || ''));
        return dt.isValid ? dt >= from && dt <= to : false;
      }
      return true;
    }
    const search = normalizeText(raw);
    const field = filterParams?.name;
    const userObj = field === 'created_at'
      ? rowData?.created_by_user
      : field === 'updated_at'
        ? rowData?.updated_by_user
        : null;
    const uname = userObj?.employee?.name || userObj?.username || '';
    const iso = String(rowData?.[field] || '');
    const dt = DateTime.fromISO(iso);
    const dateStr = dt.isValid ? `${dt.toFormat(dateFmt)} ${dt.toFormat(dateTimeFmt)} ${dt.toFormat('yyyy-MM-dd')}` : '';
    const formatted = iso && typeof formatDate === 'function' ? (formatDate(iso, dateTimeFmt, true) || '') : '';
    const hay = `${uname} ${dateStr} ${formatted}`;
    return normalizeText(hay).includes(search);
  }, [dateFmt, dateTimeFmt, formatDate]);

  const formatDateTimeCell = useCallback((raw) => {
    if (raw == null || raw === '') return '';
    if (typeof formatDate === 'function') return formatDate(raw, dateTimeFmt, true) || '';
    return formatDateUtil(raw, dateFmt, true) || '';
  }, [dateFmt, dateTimeFmt, formatDate]);

  const onSelectRow = useCallback((data) => {
    const count = Array.isArray(data) ? data.length : 0;
    const canEdit = user?.permissions?.includes('edit training-invitation');
    const canAdd = user?.permissions?.includes('add training-invitation');
    const canDelete = user?.permissions?.includes('delete training-invitation');
    setEditIcon(!canEdit || count !== 1);
    setCopyIcon(!canAdd || count !== 1);
    setDeleteIcon(!canDelete || count < 1);
  }, [user?.permissions]);

  useEffect(() => {
    ensureTrainingInvitationTemplatesLoaded({ dispatch, state });
  }, [dispatch, state.training_invitation_templates_loaded]);

  const reloadInvitationTemplates = useCallback(() => {
    refreshTrainingInvitationTemplates({ dispatch });
  }, [dispatch]);

  const deleteSelected = useCallback(() => {
    const selected = getTable()?.getSelectedData?.() ?? [];
    if (!selected.length) return;
    Promise.all(selected.map((x) => deleteRequest(`/api/training_invitation_templates/${x.id}`)))
      .then(() => {
        toast?.current?.show({ severity: 'info', summary: t('TrainingInvitationTemplateList.Toast.DeletedSummary'), detail: t('TrainingInvitationTemplateList.Toast.DeletedDetail'), life: 3000 });
        return refreshTrainingInvitationTemplates({ dispatch });
      })
      .catch((e) => {
        toast?.current?.show({ severity: 'warn', summary: t('TrainingInvitationTemplateList.Toast.RejectedSummary'), detail: e?.message || t('TrainingInvitationTemplateList.Toast.DeleteFailedDetail'), life: 3000 });
      });
  }, [dispatch, getTable, t, toast]);

  const confirmDelete = useCallback(() => {
    confirmDialog({
      message: t('ServiceTask.ConfirmDeleteRecord'),
      header: t('ServiceTask.ConfirmDeleteTitle'),
      icon: 'pi pi-info-circle',
      acceptClassName: 'p-button-danger',
      acceptLabel: t('Common.Yes'),
      rejectLabel: t('Common.No'),
      accept: deleteSelected,
    });
  }, [deleteSelected, t]);

  function onEditRow(template) {
    dispatch({ type: 'create_new_tab', name: t('TrainingInvitationTemplateList.Tab.Edit'), translationKey: 'TrainingInvitationTemplateList.Tab.Edit', legacyLabel: t('TrainingInvitationTemplateList.Tab.Edit'), page: 'TrainingInvitationTemplateAdd', props: { template, type: 'edit', not_last_used: true }, mask: ADD_MASK });
  }

  function addNewInvitationTemplate() {
    dispatch({ type: 'create_new_tab', name: t('TrainingInvitationTemplateList.Tab.New'), translationKey: 'TrainingInvitationTemplateList.Tab.New', legacyLabel: t('TrainingInvitationTemplateList.Tab.New'), page: 'TrainingInvitationTemplateAdd', props: { template: null, type: 'add' }, mask: ADD_MASK });
  }

  function copyInvitationTemplate() {
    dispatch({ type: 'create_new_tab', name: t('TrainingInvitationTemplateList.Tab.New'), translationKey: 'TrainingInvitationTemplateList.Tab.New', legacyLabel: t('TrainingInvitationTemplateList.Tab.New'), page: 'TrainingInvitationTemplateAdd', props: { template: getTable()?.getSelectedData?.()?.[0], type: 'add', not_last_used: true }, mask: ADD_MASK });
  }

  function editInvitationTemplate() {
    dispatch({ type: 'create_new_tab', name: t('TrainingInvitationTemplateList.Tab.Edit'), translationKey: 'TrainingInvitationTemplateList.Tab.Edit', legacyLabel: t('TrainingInvitationTemplateList.Tab.Edit'), page: 'TrainingInvitationTemplateAdd', props: { template: getTable()?.getSelectedData?.()?.[0], type: 'edit', not_last_used: true }, mask: ADD_MASK });
  }

  function EditRowFormatter(props) {
    const rowData = props.cell.getRow().getData();
    return (
      <div className="flex items-center justify-center w-full h-full">
        <button type="button" onClick={() => onEditRow(rowData)}>
          <EditRowIcon />
        </button>
      </div>
    );
  }

  function TruncatePlainTextFormatter(props) {
    const v = props.cell.getValue();
    const s = v != null ? String(v) : '';
    return <span className="block w-full min-w-0 truncate whitespace-nowrap" title={s || undefined}>{s}</span>;
  }

  function CreatedAtCellFormatter(props) {
    const rowData = props.cell.getRow().getData();
    const u = rowData?.created_by_user;
    const displayName = u?.employee?.name || u?.username || '';
    const text = formatDateTimeCell(rowData?.created_at);
    return (
      <div className="flex items-center justify-start w-full h-full min-w-0 gap-1">
        {u ? (
          <Avatar
            className="shrink-0"
            title={displayName}
            label={getUserInitials?.(displayName) || ''}
            shape="circle"
            style={{ backgroundColor: getUserColor?.(u?.username) || '#6b7280', color: '#ffffff' }}
          />
        ) : null}
        <div className="min-w-0 flex-1 truncate whitespace-nowrap" title={text || undefined}>{text}</div>
      </div>
    );
  }

  function UpdatedAtCellFormatter(props) {
    const rowData = props.cell.getRow().getData();
    const u = rowData?.updated_by_user;
    const displayName = u?.employee?.name || u?.username || '';
    const text = formatDateTimeCell(rowData?.updated_at);
    return (
      <div className="flex items-center justify-start w-full h-full min-w-0 gap-1">
        {u ? (
          <Avatar
            className="shrink-0"
            title={displayName}
            label={getUserInitials?.(displayName) || ''}
            shape="circle"
            style={{ backgroundColor: getUserColor?.(u?.username) || '#6b7280', color: '#ffffff' }}
          />
        ) : null}
        <div className="min-w-0 flex-1 truncate whitespace-nowrap" title={text || undefined}>{text}</div>
      </div>
    );
  }

  const columns = useMemo(() => {
    const colTitle = (key) => t(key);
    return [
      { field: 'rowSelection', formatter: 'rowSelection', titleFormatter: 'rowSelection', hozAlign: 'center', width: 44, headerSort: false, resizable: false, frozen: true, print: false, download: false },
      { title: '', field: 'editor', hozAlign: 'center', width: 44, headerSort: false, formatter: reactFormatter(<EditRowFormatter />), resizable: false, frozen: true, print: false, download: false },
      { title: colTitle('TrainingInvitationTemplateList.Column.Id'), field: 'id', minWidth: TABULATOR_LIST_ID_MIN_WIDTH, widthGrow: 0.5, hozAlign: 'left', sorter: 'number', frozen: true, headerFilter: inputOnEnterHeaderFilter, headerFilterLiveFilter: false, headerFilterFunc: idHeaderFilter },
      { title: colTitle('TrainingInvitationTemplateList.Column.Name'), field: 'name', hozAlign: 'left', formatter: reactFormatter(<TruncatePlainTextFormatter />), minWidth: tabulatorColumnMinWidthFromTitle(colTitle('TrainingInvitationTemplateList.Column.Name')), widthGrow: 1.4, headerFilter: inputOnEnterHeaderFilter, headerFilterLiveFilter: false, headerFilterFunc: textHeaderFilter },
      { title: colTitle('TrainingInvitationTemplateList.Column.Description'), field: 'description', hozAlign: 'left', formatter: reactFormatter(<TruncatePlainTextFormatter />), minWidth: tabulatorColumnMinWidthFromTitle(colTitle('TrainingInvitationTemplateList.Column.Description')), widthGrow: 1.4, headerFilter: inputOnEnterHeaderFilter, headerFilterLiveFilter: false, headerFilterFunc: textHeaderFilter },
      { title: colTitle('TrainingInvitationTemplateList.Column.Content'), field: 'content', hozAlign: 'left', formatter: reactFormatter(<TruncatePlainTextFormatter />), minWidth: tabulatorColumnMinWidthFromTitle(colTitle('TrainingInvitationTemplateList.Column.Content')), widthGrow: 1.8, headerFilter: inputOnEnterHeaderFilter, headerFilterLiveFilter: false, headerFilterFunc: textHeaderFilter },
      { title: colTitle('TrainingInvitationTemplateList.Column.CreatedAt'), field: 'created_at', hozAlign: 'left', minWidth: tabulatorDateUserColumnMinWidth(colTitle('TrainingInvitationTemplateList.Column.CreatedAt')), widthGrow: 1, formatter: reactFormatter(<CreatedAtCellFormatter />), headerFilter: dateUserHeaderFilter, headerFilterLiveFilter: false, headerFilterParams: { dateFormat: dateFmt, dateTimeFormat: dateTimeFmt }, headerFilterFunc: dateUserColumnFilter, headerFilterFuncParams: { name: 'created_at' }, sorter: 'datetime', sorterParams: { format: 'iso' } },
      { title: colTitle('TrainingInvitationTemplateList.Column.UpdatedAt'), field: 'updated_at', hozAlign: 'left', minWidth: tabulatorDateUserColumnMinWidth(colTitle('TrainingInvitationTemplateList.Column.UpdatedAt')), widthGrow: 1, formatter: reactFormatter(<UpdatedAtCellFormatter />), headerFilter: dateUserHeaderFilter, headerFilterLiveFilter: false, headerFilterParams: { dateFormat: dateFmt, dateTimeFormat: dateTimeFmt }, headerFilterFunc: dateUserColumnFilter, headerFilterFuncParams: { name: 'updated_at' }, sorter: 'datetime', sorterParams: { format: 'iso' } },
    ];
  }, [dateFmt, dateTimeFmt, dateUserColumnFilter, idHeaderFilter, t, textHeaderFilter]);

  useEffect(() => {
    const table = getTable();
    if (!table?.getColumns) return;
    applyTabulatorRuntimeI18n(table, t, {
      columnTitleKeys: INVITATION_TEMPLATE_COLUMN_I18N_KEYS,
      placeholder: t('Common.NoData'),
      reformatRows: true,
    });
  }, [locale, t, getTable]);


  const getMaskColumnLabel = useCallback((field) => {
    const key = INVITATION_TEMPLATE_MASK_FIELD_I18N[field];
    return key ? t(key) : field;
  }, [t]);

  const getPrintColumnsOrder = useCallback(() => {
    const table = getTable();
    const allowed = Object.keys(DEFAULT_COLUMNS_VISIBLE_INVITATION_TEMPLATE);
    const fallback = () => allowed.filter((f) => columnsVisible[f] !== false);
    if (!table?.getColumns) return fallback();
    const allowedSet = new Set(allowed);
    const out = [];
    const seen = new Set();
    try {
      table.getColumns().forEach((col) => {
        const field = col.getField?.() ?? col.getDefinition?.()?.field;
        if (!field || !allowedSet.has(field)) return;
        if (columnsVisible[field] === false || seen.has(field)) return;
        seen.add(field);
        out.push(field);
      });
    } catch (_) {
      return fallback();
    }
    return out.length > 0 ? out : fallback();
  }, [getTable, columnsVisible]);

  const parseHeaderFiltersToParams = useCallback(() => {
    const table = getTable();
    const hf = table?.getHeaderFilters?.() ?? [];
    const out = {};
    hf.forEach((f) => {
      if (!f?.field) return;
      const v = f.value;
      if (v == null || String(v).trim() === '') return;
      out[`filter_${f.field}`] = v;
    });
    return out;
  }, [getTable]);

  const buildInvitationTemplateExportQueryString = useCallback((opts = {}) => {
    const table = getTable();
    const params = {
      ...parseHeaderFiltersToParams(),
      toolbar_search: String(submittedSearchValueRef.current ?? '').trim() || undefined,
      sorters: JSON.stringify(cleanSorters(table?.getSorters?.() ?? [])),
    };
    return buildExportQueryStringFromParams(params, opts);
  }, [getTable, parseHeaderFiltersToParams]);

  const runInvitationTemplatePrint = useCallback(async () => {
    try {
      setPrintChoiceVisible(false);
      setDialogText(t('Common.PreparingPrint'));
      setVisible(true);
      const suffix = buildInvitationTemplateExportQueryString({
        includePrintColumns: true,
        printLayout: printLayoutChoice,
        printOrientation: printOrientationChoice,
        printColumns: getPrintColumnsOrder(),
      });
      const html = await fetchTextGet(`/api/training_invitation_templates/export/print${suffix}`);
      if (!openPrintHtmlWindow(html)) {
        toast?.current?.show({ severity: 'warn', summary: t('TrainingInvitationTemplateList.Toast.PrintTitle'), detail: t('Common.EnablePopups'), life: 4000 });
      }
    } catch (e) {
      toast?.current?.show({ severity: 'error', summary: t('TrainingInvitationTemplateList.Toast.PrintTitle'), detail: e?.message || t('TrainingInvitationTemplateList.Toast.PrintFailed'), life: 4500 });
    } finally {
      setVisible(false);
    }
  }, [buildInvitationTemplateExportQueryString, getPrintColumnsOrder, printLayoutChoice, printOrientationChoice, setDialogText, setVisible, t, toast]);

  const exportInvitationTemplateXlsx = useCallback(async () => {
    try {
      setDialogText(t('Common.PreparingExport'));
      setVisible(true);
      const suffix = buildInvitationTemplateExportQueryString({ includePrintColumns: true, printColumns: getPrintColumnsOrder() });
      const defaultXlsx = xlsxDownloadFilename(t(EXPORT_DOCUMENT_TITLE_KEYS.TRAINING_INVITATION_TEMPLATES));
      const { blob, filename } = await downloadBlobGet(`/api/training_invitation_templates/export/xlsx${suffix}`, defaultXlsx);
      triggerBlobDownload(blob, filename || defaultXlsx);
    } catch (e) {
      toast?.current?.show({ severity: 'error', summary: t('TrainingInvitationTemplateList.Toast.ExportTitle'), detail: e?.message || t('TrainingInvitationTemplateList.Toast.ExportFailed'), life: 4500 });
    } finally {
      setVisible(false);
    }
  }, [buildInvitationTemplateExportQueryString, getPrintColumnsOrder, setDialogText, setVisible, t, toast]);

  const closeMenu = () => {
    OverflowMenuProvider.setVisible(null);
    setExpandedKeys({});
  };

  const openMaskDialog = () => {
    setColumnsVisible(mergeVisibility(columnsVisible));
    setMaskVisible(true);
    closeMenu();
  };

  const saveUserMaskColumns = () => {
    const normalized = normalizeInvitationTemplateColumnVisibilityForSave(columnsVisible);
    if (!Object.values(normalized).some(Boolean)) {
      toast?.current?.show({ severity: 'warn', summary: t('TrainingInvitationTemplateList.Toast.MaskWarnTitle'), detail: t('TrainingInvitationTemplateList.Toast.SelectOneColumn'), life: 4000 });
      return;
    }
    const filtersSnapshot = getTable()?.getHeaderFilters?.() ?? [];
    setUserMaskFilter(PAGE_LIST_KEY, 'all', normalized, filtersSnapshot, null, null);
    setColumnsVisible(normalized);
    setMaskVisible(false);
  };

  const confirmSaveStandardMask = () => {
    confirmDialog({
      message: t('TrainingInvitationTemplateList.SaveStandardMaskMessage'),
      header: t('TrainingInvitationTemplateList.SaveStandardMaskHeader'),
      icon: 'pi pi-info-circle',
      acceptLabel: t('Common.Save'),
      rejectLabel: t('Common.Cancel'),
      accept: () => {
        const table = getTable();
        const layout = getTabulatorLayoutForStandardMask(table);
        if (!layout) return;
        const columnsForSave = columnsFromTabulatorForStandardMaskSave(layout.columns);
        setStandardMaskFilter(PAGE_LIST_KEY, columnsForSave, layout.attributes);
      },
    });
  };

  const confirmApplyStandardLayoutToUserMask = () => {
    closeMenu();
    const standard = (state.standard_mask_filters || []).find((m) => m.page === PAGE_LIST_KEY);
    if (!standard) {
      toast?.current?.show({ severity: 'warn', summary: t('TrainingInvitationTemplateList.Toast.StandardMaskWarnTitle'), detail: t('TrainingInvitationTemplateList.StandardMaskMissing'), life: 4000 });
      return;
    }
    const attributes = safeParse(standard.attributes, []);
    if (!Array.isArray(attributes) || attributes.length <= 1) return;
    const jsonColumns = safeParse(standard.columns, {});
    const mergedCols = mergeVisibility(jsonColumns && typeof jsonColumns === 'object' ? jsonColumns : {});
    const colsForSave = normalizeInvitationTemplateColumnVisibilityForSave(mergedCols);
    confirmDialog({
      message: t('TrainingInvitationTemplateList.ApplyStandardMaskMessage'),
      header: t('TrainingInvitationTemplateList.ApplyStandardMaskHeader'),
      icon: 'pi pi-info-circle',
      acceptLabel: t('Common.Apply'),
      rejectLabel: t('Common.Cancel'),
      accept: () => {
        setUserMaskFilter(PAGE_LIST_KEY, 'all', colsForSave, null, attributes, null, () => {
          const table = getTable();
          if (!table) return;
          isRestoringRef.current = true;
          try { table.setColumnLayout?.(attributes); } catch (_) {}
          setColumnsVisible(colsForSave);
          applyDataColumnVisibilityToTable(table, colsForSave);
          setTimeout(() => { isRestoringRef.current = false; }, 0);
        });
      },
    });
  };

  function setMask(field, value) {
    if (!Object.prototype.hasOwnProperty.call(DEFAULT_COLUMNS_VISIBLE_INVITATION_TEMPLATE, field)) return;
    const table = getTable();
    const next = { ...DEFAULT_COLUMNS_VISIBLE_INVITATION_TEMPLATE, ...columnsVisible, [field]: value };
    if (value === false && countVisibleDataColumns(next) < 1) {
      toast?.current?.show({ severity: 'warn', summary: t('TrainingInvitationTemplateList.Toast.MaskWarnTitle'), detail: t('TrainingInvitationTemplateList.Toast.AtLeastOneColumn'), life: 3500 });
      return;
    }
    applyDataColumnVisibilityToTable(table, next);
    setColumnsVisible(next);
  }

  function maskSelectAll() {
    const next = { ...DEFAULT_COLUMNS_VISIBLE_INVITATION_TEMPLATE };
    Object.keys(next).forEach((k) => { next[k] = true; });
    applyDataColumnVisibilityToTable(getTable(), next);
    setColumnsVisible(next);
  }

  function maskDeselectAll() {
    const next = {};
    Object.keys(DEFAULT_COLUMNS_VISIBLE_INVITATION_TEMPLATE).forEach((k) => { next[k] = false; });
    next.id = true;
    applyDataColumnVisibilityToTable(getTable(), next);
    setColumnsVisible(next);
  }

  const resetMask = () => resetTabulatorMask({ page: PAGE_LIST_KEY, defaultColumnsVisible: DEFAULT_COLUMNS_VISIBLE_INVITATION_TEMPLATE, ref, columns, setColumnsVisible });

  const itemRenderer = (item, options) => (
    <button type="button" className="flex align-items-center px-3 py-2 cursor-pointer items-center bg-[#333] text-white w-[180px] border-0 text-left" onClick={options.onClick}>
      <span className={`${item.icon} text-primary`} />
      <span className={`mx-2 ${item.items && 'font-semibold'}`}>{item.label}</span>
    </button>
  );

  const items = useMemo(() => [{
    key: '0',
    label: t('Common.Actions'),
    icon: 'pi pi-sliders-v',
    template: itemRenderer,
    items: [
      { key: '0_1', label: t('Common.SetFilter'), icon: 'pi pi-filter', command: () => { setUserMaskFilter(PAGE_LIST_KEY, 'set_filters', null, getTable()?.getHeaderFilters?.() ?? [], null); closeMenu(); }, template: itemRenderer },
      { key: '0_2', label: t('Common.ClearFilter'), icon: 'pi pi-filter-slash', command: () => { setUserMaskFilter(PAGE_LIST_KEY, 'set_filters', null, [], null); getTable()?.clearHeaderFilter?.(); closeMenu(); }, template: itemRenderer },
      { key: '0_3', label: t('Common.SetSorting'), icon: 'pi pi-sort-alt', command: () => { setUserMaskFilter(PAGE_LIST_KEY, 'set_sorters', null, null, null, cleanSorters(getTable()?.getSorters?.() ?? [])); closeMenu(); }, template: itemRenderer },
      { key: '0_4', label: t('Common.ClearSorting'), icon: 'pi pi-sort-alt-slash', command: () => { setUserMaskFilter(PAGE_LIST_KEY, 'set_sorters', null, null, null, []); getTable()?.setSort?.([]); closeMenu(); }, template: itemRenderer },
      { key: '0_5', label: t('Common.SetMask'), icon: 'pi pi-server', command: openMaskDialog, template: itemRenderer },
      { key: '0_5b', label: t('Common.ResetUserMask'), icon: 'pi pi-refresh', command: () => { resetMask(); closeMenu(); }, template: itemRenderer },
      ...(hasApplicableStandardMask ? [{ key: '0_5s', label: t('Common.StandardMask'), icon: 'pi pi-clone', command: confirmApplyStandardLayoutToUserMask, template: itemRenderer }] : []),
      { key: '0_6', label: t('ServiceTask.Notifications'), icon: 'pi pi-envelope', command: () => { openChosenNotificationPreferences(PAGE_LIST_KEY); closeMenu(); }, template: itemRenderer },
    ],
  }], [hasApplicableStandardMask, t]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (overlayRef.current && !overlayRef.current.contains(event.target)) setExpandedKeys({});
    };
    document.addEventListener('click', handleClickOutside, true);
    return () => document.removeEventListener('click', handleClickOutside, true);
  }, []);

  useEffect(() => {
    const el = tableContainerRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => {
      const h = el.clientHeight;
      if (h > 0) {
        const wasHidden = lastContainerHeightRef.current <= 0;
        lastContainerHeightRef.current = h;
        if (wasHidden) requestAnimationFrame(() => getTable()?.redraw?.(true));
      } else {
        lastContainerHeightRef.current = 0;
      }
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [getTable]);

  const searchTable = () => {
    const normalized = String(searchValue ?? '').trim();
    submittedSearchValueRef.current = normalized;
    setSearchIconColor(normalized === '' ? '' : 'active');
    const table = getTable();
    if (!table) return;
    if (normalized === '') table.clearFilter(true);
    else {
      const needle = normalizeText(normalized);
      table.setFilter((data) => normalizeText(JSON.stringify(data)).includes(needle));
    }
    setVisibleSearch(false);
  };

  const scrollToFirst = () => scrollToFirstUtil(getTable());
  const scrollUp = () => scrollUpUtil(getTable());
  const scrollDown = () => scrollDownUtil(getTable());
  const scrollToLast = () => scrollToLastUtil(getTable());

  return (
    <div className="h-full min-h-0 flex flex-col overflow-hidden">
      <div className="w-full h-10 flex flex-row bg-[#333] shrink-0">
        <IntersectionObserverWrapper>
          <div className="flex w-10 h-10 px-2 py-1 justify-center" data-targetid="add"><button type="button" className="disabled:opacity-25" disabled={!user?.permissions?.includes('add training-invitation')} onClick={addNewInvitationTemplate}><AddIcon /></button></div>
          <div className="flex w-10 h-10 px-2 py-1 justify-center" data-targetid="edit"><button type="button" className="disabled:opacity-25" disabled={editIcon} onClick={editInvitationTemplate}><EditIcon /></button></div>
          <div className="flex w-10 h-10 px-2 py-1 justify-center" data-targetid="copy"><button type="button" className="disabled:opacity-25" disabled={copyIcon} onClick={copyInvitationTemplate}><CopyIcon /></button></div>
          <div className="flex w-10 h-10 px-2 py-1 justify-center" data-targetid="delete"><button type="button" className="disabled:opacity-25" onClick={confirmDelete} disabled={deleteIcon}><DeleteIcon /></button></div>
          <div className="flex w-10 h-10 px-2 py-1 justify-center" data-targetid="print"><button type="button" onClick={() => setPrintChoiceVisible(true)}><PrintIcon /></button></div>
          <div className="flex w-10 h-10 px-2 py-1 justify-center" data-targetid="search">
            <button type="button" title={searchIconColor !== 'active' ? t('Common.Search') : t('ServiceTask.CancelSearch')} onClick={searchIconColor !== 'active' ? () => setVisibleSearch(true) : () => { setSearchIconColor(''); setSearchValue(''); submittedSearchValueRef.current = ''; getTable()?.clearFilter?.(true); }}>
              {searchIconColor === '' ? <MdSearch fill="#fff" className="w-[28px] h-[28px]" aria-hidden="true" /> : <MdSearchOff fill="#fff" className="w-[28px] h-[28px]" aria-hidden="true" />}
            </button>
          </div>
          <div className="flex w-10 h-10 px-2 py-1 justify-center" data-targetid="export"><button type="button" onClick={exportInvitationTemplateXlsx} title={t('Common.ExportToExcel')}><ExportIcon /></button></div>
          <div className="flex w-10 h-10 px-2 py-1 justify-center" data-targetid="refresh"><button type="button" onClick={reloadInvitationTemplates} title={t('Common.Refresh')}><RefreshIcon /></button></div>
          <div className="flex w-10 h-10 px-2 py-1 justify-center"><VerticalLineIcon /></div>
          <div className="flex w-10 h-10 px-2 py-1 justify-center"><button type="button" onClick={scrollToFirst} title={t('Icon.First')}><BackToFirstIcon /></button></div>
          <div className="flex w-10 h-10 px-2 py-1 justify-center"><button type="button" onClick={scrollUp} title={t('Icon.Previous')}><BackIcon /></button></div>
          <div className="flex w-10 h-10 px-2 py-1 justify-center"><button type="button" onClick={scrollDown} title={t('Icon.Next')}><ForwardIcon /></button></div>
          <div className="flex w-10 h-10 px-2 py-1 justify-center"><button type="button" onClick={scrollToLast} title={t('Icon.Last')}><ForwardToLastIcon /></button></div>
          <div className="flex w-10 h-10 px-2 py-1 justify-center"><VerticalLineIcon /></div>
          <div className="flex w-10 h-10 px-2 py-1 justify-center"><button type="button" onClick={() => openFavoriteDialog(t('TrainingInvitationTemplateList.FavoriteTitle'), PAGE_LIST_KEY)}><FavoriteIcon /></button></div>
          <div className="flex w-10 h-10 px-2 py-1 justify-center"><button type="button" onClick={() => setFastView(PAGE_LIST_KEY, columnsVisible, columns)}><PriceIcon /></button></div>
          {canSetStandardMask ? (
            <div className="flex w-10 h-10 px-2 py-1 justify-center">
              <button type="button" title={t('Common.SaveStandardMask')} aria-label={t('Common.SaveStandardMask')} onClick={confirmSaveStandardMask} className="flex items-center justify-center">
                <i className="pi pi-bookmark text-white text-xl" aria-hidden="true" />
              </button>
            </div>
          ) : null}
          <div className="flex w-10 h-10 px-2 py-1 justify-center"><VerticalLineIcon /></div>
          <div className="card flex justify-content-center z-[100]" data-targetid="actions" ref={overlayRef}>
            <PanelMenu model={items} expandedKeys={expandedKeys} onExpandedKeysChange={setExpandedKeys} className="w-full md:w-20rem" />
          </div>
        </IntersectionObserverWrapper>
      </div>

      <div className="px-1 bg-white rounded-md shadow-md flex-1 min-h-0 overflow-hidden">
        <div ref={tableContainerRef} className="flex-1 min-h-0 overflow-hidden">
          <ReactTabulator
            key="invitation-template-list"
            data={state.training_invitation_templates || []}
            className="h-[calc(100vh_-_193px)]"
            onRef={(r) => { ref.current = r?.current; }}
            columns={columns}
            events={{
              rowSelectionChanged: onSelectRow,
              tableBuilt: () => {
                const table = getTable();
                if (!table) return;
                const effective = getEffectiveMask(PAGE_LIST_KEY, state);
                if (effective?.type === 'user' && effective.userMask) {
                  const m = effective.userMask;
                  isRestoringRef.current = true;
                  applyUserMaskRowToTabulator(table, m, {
                    setColumnsVisible: (v) => setColumnsVisible(mergeVisibility(v)),
                    clearHeaderFiltersWhenEmpty: true,
                    forceFrozenId: true,
                    applyColumnVisibilityFn: (tbl, jc, setVis) => {
                      const merged = mergeVisibility(jc);
                      setVis(merged);
                      applyDataColumnVisibilityToTable(tbl, merged);
                    },
                  });
                  {
                    const merged = mergeVisibility(safeParse(m.columns, {}));
                    for (const field of Object.keys(DEFAULT_COLUMNS_VISIBLE_INVITATION_TEMPLATE)) {
                      if (merged[field] === false) clearHeaderFilterForField(table, field);
                    }
                  }
                  setTimeout(() => { isRestoringRef.current = false; }, 0);
                } else if (effective?.type === 'standard' && effective.standardMask) {
                  const m = effective.standardMask;
                  isRestoringRef.current = true;
                  const attributes = safeParse(m.attributes, []);
                  const jsonColumns = safeParse(m.columns, {});
                  if (Array.isArray(attributes) && attributes.length > 1) table.setColumnLayout?.(attributes);
                  if (jsonColumns && typeof jsonColumns === 'object') {
                    const merged = mergeVisibility(jsonColumns);
                    setColumnsVisible(merged);
                    applyDataColumnVisibilityToTable(table, merged);
                  }
                  setTimeout(() => { isRestoringRef.current = false; }, 0);
                } else {
                  applyDataColumnVisibilityToTable(table, DEFAULT_COLUMNS_VISIBLE_INVITATION_TEMPLATE);
                }
              },
              ...getTabulatorPersistLayoutEvents(PAGE_LIST_KEY, ref, setUserMaskFilter),
            }}
            options={{
              renderHorizontal: 'virtual',
              layout: 'fitColumns',
              movableColumns: true,
              autoResize: true,
              headerSortTristate: true,
              filterMode: 'local',
              sortMode: 'local',
              height: TABLE_HEIGHT,
            }}
          />
        </div>
      </div>

      <PrintOptionsDialog
        visible={printChoiceVisible}
        onHide={() => setPrintChoiceVisible(false)}
        orientation={printOrientationChoice}
        onOrientationChange={setPrintOrientationChoice}
        layout={printLayoutChoice}
        onLayoutChange={setPrintLayoutChoice}
        onConfirm={runInvitationTemplatePrint}
      />

      <Dialog header={t('TrainingInvitationTemplateList.SearchDialogTitle')} visible={visibleSearch} style={{ width: '40vw' }} onHide={() => setVisibleSearch(false)}>
        <div className="flex flex-col">
          <p className="text-sm text-gray-600 mb-2">{t('TrainingInvitationTemplateList.SearchHint')}</p>
          <div className="flex items-center gap-3 w-full">
            <input value={searchValue} onChange={(e) => setSearchValue(e.target.value)} type="text" className="flex-1 h-10 px-4 border rounded-md focus:border-gray-400" />
            <button type="button" onClick={searchTable} className="h-10 w-16 bg-zinc-900 text-white rounded-md hover:bg-zinc-800 flex items-center justify-center"><SearchIcon /></button>
          </div>
        </div>
      </Dialog>

      <TabulatorMaskDialog
        visible={maskVisible}
        onHide={() => setMaskVisible(false)}
        title={t('TrainingInvitationTemplateList.MaskDialogTitle')}
        fields={Object.keys(DEFAULT_COLUMNS_VISIBLE_INVITATION_TEMPLATE)}
        checkedMap={columnsVisible}
        onToggleField={setMask}
        onSelectAll={maskSelectAll}
        onDeselectAll={maskDeselectAll}
        onSave={saveUserMaskColumns}
        selectAllLabel={t('TrainingInvitationTemplateList.MaskSelectAll')}
        deselectAllLabel={t('TrainingInvitationTemplateList.MaskDeselectAll')}
        saveLabel={t('TrainingInvitationTemplateList.MaskSave')}
        getFieldLabel={getMaskColumnLabel}
        inputIdPrefix="invitation-template-mask"
      />
    </div>
  );
}
